// ═══════════════════════════════════════════════════════
// EMPTY STATES — exibido quando cliente ainda não tem snapshot
// ═══════════════════════════════════════════════════════
import { useClientContext } from "../ClientContext.jsx";
import { RADIUS, TYPE, SP, LIGHT } from "../shared/theme.js";
import { safeText } from "../shared/formatters.js";

// ── Interfaces ─────────────────────────────────────────
interface EmptyStatePageProps {
  label: string;
}

// ── Components ─────────────────────────────────────────

export function EmptyStateDashboard() {
  const { T, client, setPage } = useClientContext();
  const onGoToDocumentos = () => setPage("documentos");
  return (
    <div>
      {/* Hero */}
      <div
        style={{
          background: `linear-gradient(135deg,${T.accent} 0%,${T.accentHover} 100%)`,
          borderRadius: RADIUS.lg,
          padding: `${SP[8]} ${SP[7]}`,
          marginBottom: SP[6],
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: TYPE["3xl"], marginBottom: SP[3] }}>📊</div>
        <h2
          style={{
            fontSize: TYPE["2xl"],
            fontWeight: TYPE.heavy,
            color: T.accentText,
            margin: `0 0 ${SP[2]}`,
          }}
        >
          Bem-vindo ao LPA Wealth Portal
          {(client as Record<string, unknown>)?.shortName
            ? `, ${safeText((client as Record<string, unknown>).shortName as string)}`
            : ""}
          !
        </h2>
        <p
          style={{
            fontSize: TYPE.base,
            color: T.accentText,
            margin: `0 0 ${SP[6]}`,
            maxWidth: 480,
            marginLeft: "auto",
            marginRight: "auto",
            opacity: 0.65,
          }}
        >
          Seu painel está pronto. Assim que enviar seu primeiro extrato, seus dados de patrimônio e
          performance aparecerão aqui automaticamente.
        </p>
        <button
          onClick={onGoToDocumentos}
          style={{
            padding: `${SP[3]} ${SP[7]}`,
            borderRadius: RADIUS.md,
            border: "none",
            cursor: "pointer",
            background: LIGHT.gold,
            color: T.accentText,
            fontWeight: TYPE.heavy,
            fontSize: TYPE.md,
            letterSpacing: 0.3,
          }}
        >
          📂 Enviar primeiro extrato
        </button>
      </div>

      {/* Cards placeholder */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
          gap: SP[3],
          marginBottom: SP[5],
        }}
      >
        {["Patrimônio Total", "Retorno 12M", "Ganho Financeiro", "Volatilidade"].map((l) => (
          <div
            key={l}
            style={{
              background: T.card,
              border: `2px dashed ${T.border}`,
              borderRadius: RADIUS.md,
              padding: `${SP[5]} ${SP[4]}`,
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: TYPE.xs,
                fontWeight: TYPE.semibold,
                color: T.sub,
                textTransform: "uppercase",
                letterSpacing: 0.8,
                marginBottom: SP[2],
              }}
            >
              {l}
            </div>
            <div style={{ fontSize: TYPE["2xl"], fontWeight: TYPE.heavy, color: T.border }}>—</div>
          </div>
        ))}
      </div>

      {/* Steps */}
      <div
        style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.lg,
          padding: `${SP[6]} ${SP[7]}`,
        }}
      >
        <div
          style={{ fontSize: TYPE.md, fontWeight: TYPE.heavy, color: T.text, marginBottom: SP[5] }}
        >
          Como funciona
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
            gap: SP[4],
          }}
        >
          {[
            {
              n: "1",
              icon: "📂",
              title: "Envie seu extrato",
              desc: "Arraste qualquer extrato XP, BTG, Bradesco, Itaú, Santander ou outro banco.",
            },
            {
              n: "2",
              icon: "🔍",
              title: "Detecção automática",
              desc: "O sistema lê o arquivo e identifica seu patrimônio instantaneamente.",
            },
            {
              n: "3",
              icon: "📈",
              title: "Dashboard atualizado",
              desc: "Seus dados de performance, alocação e retorno ficam disponíveis em segundos.",
            },
          ].map((s) => (
            <div key={s.n} style={{ textAlign: "center", padding: `${SP[1]} ${SP[2]}` }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: RADIUS.full,
                  background: LIGHT.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: TYPE.xl,
                  margin: `0 auto ${SP[2]}`,
                }}
              >
                {s.icon}
              </div>
              <div
                style={{
                  fontSize: TYPE.sm,
                  fontWeight: TYPE.heavy,
                  color: T.text,
                  marginBottom: SP[1],
                }}
              >
                {s.title}
              </div>
              <div style={{ fontSize: TYPE.xs, color: T.sub, lineHeight: TYPE.normal }}>
                {s.desc}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function EmptyStatePage({ label }: EmptyStatePageProps) {
  const { T, setPage } = useClientContext();
  const onGoToDocumentos = () => setPage("documentos");
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 380,
        textAlign: "center",
        padding: SP[8],
      }}
    >
      <div style={{ fontSize: TYPE["3xl"], marginBottom: SP[4] }}>📋</div>
      <h3
        style={{ fontSize: TYPE.xl, fontWeight: TYPE.heavy, color: T.text, margin: `0 0 ${SP[2]}` }}
      >
        {label}
      </h3>
      <p style={{ fontSize: TYPE.base, color: T.sub, margin: `0 0 ${SP[6]}`, maxWidth: 360 }}>
        Esta seção ficará disponível assim que seu primeiro extrato for processado.
      </p>
      <button
        onClick={onGoToDocumentos}
        style={{
          padding: `${SP[2]} ${SP[6]}`,
          borderRadius: RADIUS.md,
          border: "none",
          cursor: "pointer",
          background: T.accent,
          color: T.accentText,
          fontWeight: TYPE.bold,
          fontSize: TYPE.base,
        }}
      >
        📂 Enviar extrato agora
      </button>
    </div>
  );
}
